import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDataWAuth } from "../../../utils/api-service";
import {
  ADMIN_IB_TASKS_PROGRESS,
  CCM_DASHBOARD_AUTO_APPROVAL_RATE,
  CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT,
} from "../../../constants/constant";

export const fetchIbTasksProgress = createAsyncThunk(
  "adminDashboard/fetchIbTasksProgress",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(ADMIN_IB_TASKS_PROGRESS);
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch IB tasks progress");
    }
  }
);

export const fetchAdminAutoApprovalRate = createAsyncThunk(
  "adminDashboard/fetchAdminAutoApprovalRate",
  async ({ country, year, month }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (country && country !== 'ALL') {
        params.append('country', country);
      } else if (country === 'ALL') {
        params.append('country', 'ALL');
      }
      if (year) {
        params.append('year', year);
      }
      if (month) {
        params.append('month', month);
      }

      const url = params.toString() 
        ? `${CCM_DASHBOARD_AUTO_APPROVAL_RATE}?${params.toString()}`
        : CCM_DASHBOARD_AUTO_APPROVAL_RATE;

      const response = await getDataWAuth(url);
      return response.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch auto approval rate");
    }
  }
);

export const fetchAvailableMonthsForApprovalRate = createAsyncThunk(
  "adminDashboard/fetchAvailableMonthsForApprovalRate",
  async (_, { rejectWithValue }) => {
    try {
      const url = `${CCM_DASHBOARD_AUTO_APPROVAL_RATE}/availableMonths`;
      const response = await getDataWAuth(url);
      // Handle null response or 204 No Content
      if (!response || !response.data) {
        return [];
      }
      return response.data || [];
    } catch (error) {
      // Don't reject on empty data, just return empty array
      return [];
    }
  }
);

export const fetchAvailableYearsForApprovalRate = createAsyncThunk(
  "adminDashboard/fetchAvailableYearsForApprovalRate",
  async (_, { rejectWithValue }) => {
    try {
      const url = `${CCM_DASHBOARD_AUTO_APPROVAL_RATE}/availableYears`;
      const response = await getDataWAuth(url);
      // Handle null response or 204 No Content
      if (!response || !response.data) {
        return { yearsWithMonths: {} };
      }
      // Response is now a Map of year -> months[]
      return { yearsWithMonths: response.data || {} };
    } catch (error) {
      // Don't reject on empty data, just return empty object
      return { yearsWithMonths: {} };
    }
  }
);

export const fetchAdminMonthlyExtensionReport = createAsyncThunk(
  "adminDashboard/fetchAdminMonthlyExtensionReport",
  async ({ country }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (country && country !== 'ALL') {
        params.append('country', country);
      } else if (country === 'ALL') {
        params.append('country', 'ALL');
      }

      const url = params.toString()
        ? `${CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT}?${params.toString()}`
        : CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT;

      const response = await getDataWAuth(url);
      return response.data || null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch monthly extension report"
      );
    }
  }
);

const initialState = {
  ibTasksProgress: [],
  autoApprovalRate: null,
  availableMonths: [],
  availableYears: [],
  yearsWithMonths: {}, // Map of year -> months[]
  availableMonthsForYear: [],
  monthlyExtensionReport: null,
  loading: false,
  autoApprovalRateLoading: false,
  availableMonthsLoading: false,
  availableYearsLoading: false,
  availableMonthsForYearLoading: false,
  monthlyExtensionReportLoading: false,
  error: null,
};

const adminDashboardSlice = createSlice({
  name: "adminDashboard",
  initialState,
  reducers: {
    clearAdminDashboardError: (state) => {
      state.error = null;
    },

    resetAdminDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIbTasksProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIbTasksProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.ibTasksProgress = action.payload;
      })
      .addCase(fetchIbTasksProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchAdminAutoApprovalRate.pending, (state) => {
        state.autoApprovalRateLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminAutoApprovalRate.fulfilled, (state, action) => {
        state.autoApprovalRateLoading = false;
        state.autoApprovalRate = action.payload;
      })
      .addCase(fetchAdminAutoApprovalRate.rejected, (state, action) => {
        state.autoApprovalRateLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchAvailableMonthsForApprovalRate.pending, (state) => {
        state.availableMonthsLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailableMonthsForApprovalRate.fulfilled, (state, action) => {
        state.availableMonthsLoading = false;
        state.availableMonths = action.payload;
      })
      .addCase(fetchAvailableMonthsForApprovalRate.rejected, (state, action) => {
        state.availableMonthsLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchAvailableYearsForApprovalRate.pending, (state) => {
        state.availableYearsLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailableYearsForApprovalRate.fulfilled, (state, action) => {
        state.availableYearsLoading = false;
        // Extract years and store the full map
        const yearsWithMonths = action.payload.yearsWithMonths || {};
        // Sort years in descending order (2026, 2025, 2024...)
        state.availableYears = Object.keys(yearsWithMonths).sort((a, b) => b - a);
        state.yearsWithMonths = yearsWithMonths;
      })
      .addCase(fetchAvailableYearsForApprovalRate.rejected, (state, action) => {
        state.availableYearsLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchAdminMonthlyExtensionReport.pending, (state) => {
        state.monthlyExtensionReportLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminMonthlyExtensionReport.fulfilled, (state, action) => {
        state.monthlyExtensionReportLoading = false;
        state.monthlyExtensionReport = action.payload;
      })
      .addCase(fetchAdminMonthlyExtensionReport.rejected, (state, action) => {
        state.monthlyExtensionReportLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearAdminDashboardError,
  resetAdminDashboard,
} = adminDashboardSlice.actions;

export default adminDashboardSlice.reducer;
